import { FCEvents } from '@fc/events';
import { patchDOM } from '@fc/dom-patch';
import { normalizeHTMLRoot } from '@fc/section-response';

const INVENTORY_TAG = 'stock-status';
const INVENTORY_SCOPE_SELECTOR = '.fit-section, .shopify-section, dialog';
const FALLBACK_SCOPE_SELECTOR = 'section';

class ProductInventory extends HTMLElement {
  #eventsController = null;
  #eventScope = null;

  connectedCallback() {
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#unbindEvents();
  }

  #bindEvents() {
    this.#unbindEvents();
    this.#eventScope = this.#resolveEventScope();
    this.#eventsController = new AbortController();

    this.#eventScope.addEventListener(FCEvents.variantChange, this.#onVariantChange, {
      signal: this.#eventsController.signal,
      passive: true,
    });
  }

  #unbindEvents() {
    this.#eventsController?.abort();
    this.#eventsController = null;
    this.#eventScope = null;
  }

  #resolveEventScope() {
    return this.closest(INVENTORY_SCOPE_SELECTOR) ?? this.closest(FALLBACK_SCOPE_SELECTOR) ?? document.body;
  }

  get productId() {
    return this.dataset.productId;
  }

  set productId(value) {
    if (value) {
      this.dataset.productId = String(value);
    } else {
      delete this.dataset.productId;
    }
  }

  /**
   * @param {Event & { detail?: { data?: { html?: string | Document | DocumentFragment | Element, productId?: string | number, newProduct?: { id?: string | number } } } }} event
   */
  #onVariantChange = (event) => {
    const eventData = event?.detail?.data;
    if (!eventData) return;

    if (eventData.newProduct?.id) {
      this.productId = String(eventData.newProduct.id);
    }

    const sourceProductId = this.#resolveSourceProductId(event, eventData);
    if (sourceProductId && this.productId && sourceProductId !== this.productId) return;

    const sourceRoot = this.#normalizePayloadRoot(eventData.html);
    if (!sourceRoot) return;

    const nextInventoryNode = this.#resolveInventoryNode(sourceRoot);
    if (nextInventoryNode) {
      patchDOM(this, nextInventoryNode, { childrenOnly: true });
    }
  };

  /**
   * @param {Event} event
   * @param {{ productId?: string | number }} data
   * @returns {string | null}
   */
  #resolveSourceProductId(event, data) {
    if (event.target instanceof HTMLElement && event.target.dataset.productId) {
      return event.target.dataset.productId;
    }
    if (data.productId) return String(data.productId);
    return null;
  }

  /**
   * @param {string | Document | DocumentFragment | Element | undefined} payload
   * @returns {Document | DocumentFragment | Element | null}
   */
  #normalizePayloadRoot(payload) {
    return normalizeHTMLRoot(payload);
  }

  /**
   * @param {Document | DocumentFragment | Element} sourceRoot
   * @returns {Element | null}
   */
  #resolveInventoryNode(sourceRoot) {
    if (sourceRoot instanceof Element && sourceRoot.matches(INVENTORY_TAG)) {
      return sourceRoot;
    }
    if (typeof sourceRoot.querySelector !== 'function') return null;
    return sourceRoot.querySelector(INVENTORY_TAG);
  }
}

if (!customElements.get(INVENTORY_TAG)) {
  customElements.define(INVENTORY_TAG, ProductInventory);
}
